import { createContext, useContext } from "react";
import type { McpSummaryData } from "../model/Model";

/** How often the badge re-checks while the tab is visible and in use. */
export const MCP_POLL_MS = 90_000;

/** Under this much time left, a pending change is worth shouting about. */
export const MCP_URGENT_MS = 30 * 60 * 1000;

export interface McpStatusContextValue {
  summary: McpSummaryData | null;
  /** Approvals waiting right now. 0 when unknown, so nothing over-claims. */
  pending: number;
  /** True when the soonest pending change lapses within the urgent window. */
  urgent: boolean;
  /** Re-read the counts now. Called after any decision on the connections page. */
  refresh: () => Promise<void>;
}

export const McpStatusContext = createContext<McpStatusContextValue | undefined>(
  undefined
);

/**
 * Pending-approval counts, safe to call from anywhere in the shell.
 *
 * Returns an inert value outside the provider rather than throwing. This is
 * consumed by chrome that renders on every page, including the logged-out
 * marketing layout, and a badge is never important enough to blank a page.
 */
export function useMcpStatus(): McpStatusContextValue {
  return (
    useContext(McpStatusContext) ?? {
      summary: null,
      pending: 0,
      urgent: false,
      refresh: async () => {},
    }
  );
}
